import React from "react"

import { RouteComponentProps, useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import AlertMessageBox from "~tailwindui/Basics/AlertMessageBox"
import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"
import Table from "~tailwindui/Table/Table"

import emptyFunction from "src/emptyFunction"
import titlecase from "src/titlecase"
import { MatchParams } from "src/types/MatchParams"

import LocalTime from "components/LocalTime"
import DisplayableFormikForm from "components/OrganizationAdmin/components/DisplayableFormikForm"
import DonutChart from "components/OrganizationAdmin/components/DonutChart"
import Form from "components/OrganizationAdmin/components/Form"
import { studentPresenceIcon } from "components/OrganizationAdmin/components/presenceIcon"

const SessionDetails: React.FC<RouteComponentProps<MatchParams>> = ({
  match: {
    params: { id },
  },
}) => {
  const history = useHistory()
  const [result] = useQuery({
    query: sessionsQuery,
    variables: { id },
  })

  const statusMessage = ({ statusState, canceledBy, cancellationReason }) => {
    switch (statusState) {
      case "initial":
      case "requested":
        return "Note: The details of this session have not yet been finalized."
      case "scheduled":
        return ""
      case "substitute_requested":
        return "Note: A substitute has been requested for this session."
      case "canceled":
        return `Note: This session was canceled by the ${canceledBy} for the following reason: "${cancellationReason}"`
      case "declined":
        return "Note: This session was declined by the tutor."
    }
  }

  const buildChart = ({ studentSessions, statusState }) => {
    if (studentSessions.length < 2 || statusState !== "scheduled") return null
    const noShows = studentSessions.filter(
      studentSession => studentSession.noShow
    ).length
    const excusedAbsences = studentSessions.filter(
      studentSession => studentSession.excusedAbsence
    ).length
    const presences = studentSessions.filter(
      studentSession => !studentSession.noShow && !studentSession.excusedAbsence
    ).length
    return (
      <DonutChart
        title="Attendance"
        classNames="mt-10"
        data={[
          {
            value: excusedAbsences,
            name: `Excused (${excusedAbsences})`,
          },
          {
            value: presences,
            name: `Present (${presences})`,
          },
          {
            value: noShows,
            name: `No Shows (${noShows})`,
          },
        ]}
      />
    )
  }

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { viewer, airtutorsSession } }) =>
        airtutorsSession === null ? (
          <AlertMessageBox>
            You do not have access to this session
          </AlertMessageBox>
        ) : (
          <>
            <DisplayableFormikForm.Wrapper
              editable={false}
              initialValues={airtutorsSession}
              title={`${airtutorsSession.subject.name} Session${
                airtutorsSession.tutor
                  ? ` with ${airtutorsSession.tutor.fullName}`
                  : ""
              }`}
              displayData={airtutorsSession}
            >
              <>
                {(airtutorsSession.tutorFeedback ||
                  airtutorsSession.topicsCovered) && (
                  <blockquote className="border-l-4 border-gray-300 bg-gray-50 p-4">
                    <p className="text-xl font-medium italic leading-relaxed text-gray-900">
                      "{airtutorsSession.tutorFeedback}"
                    </p>
                    <p className="text-lg font-light italic">
                      ~ {airtutorsSession.tutor.fullName}
                    </p>
                    <br />
                    <p className="text-lg font-light italic">
                      Topics Covered: {airtutorsSession.topicsCovered}
                    </p>
                  </blockquote>
                )}

                {airtutorsSession.future && (
                  <div className="text-base font-medium italic text-gray-500">
                    This session has not happened yet.
                  </div>
                )}
                <div className="text-base font-medium italic text-red-500">
                  {statusMessage(airtutorsSession)}
                </div>

                <DisplayableFormikForm.Section title="Session Information">
                  <DisplayableFormikForm.TextInput
                    label="Subject"
                    name="subject.name"
                  />
                  <DisplayableFormikForm.TextInput
                    label="Tutor"
                    name="tutor.fullName"
                  />
                  {airtutorsSession.studyGroup && (
                    <DisplayableFormikForm.TextInput
                      label="Study Group"
                      name="studyGroup.name"
                    />
                  )}
                  <>
                    <Form.ValueDisplay
                      label="Start Time"
                      value={
                        <LocalTime
                          timestamp={airtutorsSession.startsAt}
                          format="ddd lll"
                        />
                      }
                    />
                    {airtutorsSession.formattedActualDuration ? (
                      <DisplayableFormikForm.TextInput
                        label="Duration"
                        name="formattedActualDuration"
                      />
                    ) : (
                      <Form.ValueDisplay
                        label="Scheduled Duration"
                        value={`${airtutorsSession.scheduledDurationMinutes} minutes`}
                      />
                    )}
                    <Form.ValueDisplay
                      label="Status"
                      value={airtutorsSession.statusState}
                    />
                    {viewer.canOrganizationAdminAccessFinances && (
                      <Form.ValueDisplay
                        label="Billing Status"
                        value={airtutorsSession.billState}
                      />
                    )}
                  </>
                </DisplayableFormikForm.Section>
              </>
            </DisplayableFormikForm.Wrapper>
            {buildChart(airtutorsSession)}
            <Table
              title="Students"
              data={airtutorsSession.studentSessions}
              changePage={emptyFunction}
              rowCallback={studentSession =>
                history.push(`/students/${studentSession.student.id}`)
              }
              cols={[
                {
                  header: "Name",
                  value: studentSession => studentSession.student.fullName,
                },
                {
                  header: "Present?",
                  value: studentSession =>
                    studentPresenceIcon(studentSession, airtutorsSession),
                },
                {
                  header: "Participation",
                  value: studentSession =>
                    titlecase(studentSession.participationLevel),
                },
                {
                  header: "Tutor Assessment",
                  value: studentSession =>
                    titlecase(studentSession.tutorAssessment),
                },
                {
                  header: "Cancellation/Absence Details",
                  value: studentSession => studentSession.cancellationReason,
                },
              ]}
            />
          </>
        )
      }
    </WithLoadingIndicator>
  )
}

const sessionsQuery = gql`
  query session($id: ID!) {
    viewer {
      canOrganizationAdminAccessFinances
    }
    airtutorsSession(id: $id) {
      id
      startsAt
      endsAt
      future
      statusState
      billState
      tutorFeedback
      studentFeedback
      scheduledDurationMinutes
      formattedBilledDuration
      formattedActualDuration
      canceledBy
      cancellationReason
      noShow
      missingTutor
      topicsCovered
      studyGroup {
        id
        name
        school {
          id
          name
        }
      }
      tutor {
        id
        fullName
      }
      subject {
        id
        name
      }
      studentSessions {
        id
        noShow
        excusedAbsence
        canceledAt
        cancellationReason
        participationLevel
        tutorAssessment
        student {
          id
          fullName
        }
      }
    }
  }
`

export default SessionDetails
